/**
 * A modal window is a graphical control element subordinate to an application&#x27;s main window. It creates a mode that disables the main window but keeps it visible, with the dialog window as a child window in front of it
 *
 * @module views/Layouts/OverlayFlyout
 */
import './OverlayFlyout.scss';

import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { FocusScope } from '@react-aria/focus';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Flyout as PalFlyout } from 'web-palette/dist/components/Flyout/Flyout';
import { STYLES as PALETTE_STYLES } from 'web-palette/dist/util/constants/styles';

import Button from '@ulta/core/components/Button/Button';
import UseKeyPress from '@ulta/core/hooks/useKeyPress/UseKeyPress';
import { ARIA_LIVE_LOCATION, AriaLiveRegion } from '@ulta/core/providers/AriaLiveRegionProvider/AriaLiveRegionProvider';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import constants from '@ulta/core/utils/constants/constants';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { isFunction } from '@ulta/core/utils/types/types';

import * as utils from './OverlayFlyout';

/**
* Represents a OverlayFlyout component
* @type {React.FC<OverlayFlyoutProps>}
*/
const OverlayFlyout = forwardRef( ( props, ref ) => {
  const {
    alignment = defaultProps.alignment,
    ariaLabel,
    ariaLabelledby,
    backAction,
    backActionCallback,
    children,
    closeAccessibilityLabel = defaultProps.closeAccessibilityLabel,
    crossButtonVisibility,
    customClassName,
    disableClose,
    onClose,
    showDynamicFlyoutHeader,
    headerText,
    customCloseEvent,
    invokeAction
  } = props;
  const { closeOverlay } = useOverlay();
  const buttonRef =  useRef();

  const [isCustom, offsetPx, isOpen] = useCustomOverlay( props, ref );

  // Auto Focus the cross button for Keyboard users.
  useEffect( ()=>{
    buttonRef.current?.focus();
  }, [] );


  if( isServer() ){
    return null;
  }
  /*
   * If we're inside a dynamic overlay, it means that we're loading content into
   * OverLayContainer and that component will handle displaying a dynamic overlay header
   * with a potential back action
   */
  const showLocalFlyoutHeader = crossButtonVisibility && !showDynamicFlyoutHeader;
  const showCloseButton = !disableClose && showLocalFlyoutHeader;
  const showBackButton = !!backAction && showLocalFlyoutHeader;
  const variant = showLocalFlyoutHeader ? utils.PALETTE_VARIANT.DEFAULT : utils.PALETTE_VARIANT.CUSTOM;
  const isBackActionFlyout = backAction?.navigationType === 'flyout';

  const formattedCloseActionElement = ( showCloseButton &&
    <>
      <AriaLiveRegion location={ ARIA_LIVE_LOCATION.Overlay } />
      <Button
        variant='unstyled'
        className={ PALETTE_STYLES.OVERLAY.HEADER_CLOSE_BUTTON }
        ariaLabel={ closeAccessibilityLabel }
        onClick={ customCloseEvent ? onClose : () => closeOverlay( { userCancelled: true } ) }
        ref={ buttonRef }
        iconName='X'
        iconSize='xl'
        ariaHiddenIcon={ true }
      />
    </>
  );

  const formattedBackActionElement = ( showBackButton &&
    <Button
      variant='linkSecondary'
      ariaLabel={ backAction?.label }
      label={ backAction?.label }
      { ...spreadProps( {
        onClick: !isBackActionFlyout ? function handleOverlayBackAction(){
          utils.handleOverlayBackAction( { backAction }, { invokeAction, backActionCallback } );
        } : undefined,
        action: isBackActionFlyout && backAction
      } ) }
      iconName='CaretBack'
      iconSize='xl'
      iconLeft={ true }
      ariaHiddenIcon={ true }
    />
  );

  const customFlyoutStyle = {
    '--topOffset': isCustom ? `${offsetPx}` : 0
  };

  const component = (
    <>
      <div className='OverlayFlyout'
        style={ customFlyoutStyle }
      >
        <FocusScope
          contain={ true }
          restoreFocus={ true }
          autoFocus={ true }
        >
          <PalFlyout
            externalCloseActionElement={ formattedCloseActionElement }
            externalBackActionElement={ formattedBackActionElement }
            showBackButton={ showBackButton }
            title={ headerText }
            ariaLabel={ ariaLabel }
            ariaLabelledBy={ ariaLabelledby }
            panelRef={ ref }
            isOpen={ isOpen }
            variant={ variant }
            alignment={ utils.PALETTE_CONTENT_ALIGNMENT[alignment] }
            className={ classNames( {
              [ `OverlayFlyout--dynamic` ]: showDynamicFlyoutHeader,
              [ `OverlayFlyout--UtilityLinks` ]: customClassName,
              [ `${customClassName}` ]: customClassName && isOpen
            } ) }
          >
            { children }
          </PalFlyout>
        </FocusScope>
      </div>
    </>
  );

  return createPortal( component, document.body );
} );

/**
 * Manages custom flyout behavior and polyfills dialog-like features for the flyout element.
 *
 * Handles:
 * - Top offset (offsetElement)
 * - Flyout animation timing
 * - Close events (ESC, backdrop click, custom close)
 * - Polyfilled `.close()` method
 * - Backdrop reference
 *
 * @param {object} props - Props passed to OverlayFlyout.
 * @param {boolean} [props.disableClose] - Prevents closing via ESC/backdrop.
 * @param {function} props.onClose - Called when overlay is closed.
 * @param {object} [props.offsetElement] - Ref with `getBoundingClientRect()` for offset positioning.
 * @param {React.RefObject} [ref={}] - Forwarded flyout ref where `.close()` will be patched.
 *
 * @returns {[boolean,string,boolean,React.RefObject,function]}
 * An array containing:
 * - `isCustom` {boolean} — Whether offset-based custom positioning is used.
 * - `offsetPx` {string} — Calculated top offset in pixels.
 * - `isOpen` {boolean} — Whether the flyout is currently open.
 * - `overlayBackdrop` {RefObject} — Backdrop reference.
 * - `handleDimmerClose` {function} — Click handler for Dimmer onClick.
 */

export const useCustomOverlay = ( props, ref = {} ) => {
  const { disableClose, onClose, offsetElement } = props;

  const [isCustom, setIsCustom] = useState( false );
  const [offsetPx, setOffsetPx] = useState( '' );

  const [isOpen, setIsOpen] = useState( false );

  // We only need to update the offset if the element changes
  useEffect( () => {
    return utils.initiallizeOverlayOffset( { offsetElement }, { setOffsetPx, setIsCustom } );
  }, [offsetElement] );

  useEffect( () => {
  // Allow time for DOM to update so the initial element can render off screen, then
    // when we flip this to true it will animate in
    const timeOut = setTimeout( () => {
      setIsOpen( true );
    }, 0 );

    return () => clearTimeout( timeOut );
  }, [] );

  const onOverlayClose = useCallback( ( e, data ) => {
    return utils.composeOnOverlayClose( { disableClose, e, data }, { onClose, setIsOpen } );
  }, [onClose, setIsOpen, disableClose] );

  useEffect( () => {
    return utils.initializeReferenceClose( { ref }, { onOverlayClose } );
  }, [onOverlayClose, disableClose] );

  // Handle ESC key
  UseKeyPress( constants.ESC_KEY, { current: document }, ( e ) => {
    return utils.composeOnUserKeyPress( { disableClose, e }, { onOverlayClose } );
  } );

  return [isCustom, offsetPx, isOpen];
};


/**
 * @method initializeReferenceClose
 * @summary function initializes the close function of the reference object
 * @param {object} data - Object which contains data.
 * @param {object} methods - an object containing methods to be passed into the function.
 */
export const initializeReferenceClose = ( data, methods ) => {
  const { ref } = data;
  const { onOverlayClose } = methods;
  if( !ref?.current ){
    return;
  }

  // Polyfill the .close function on the element
  // we only set this once because if overlay content gets replaced we will have lost the og reference
  if( !isFunction( ref?.current?.close ) ){

    ref.current.close = onOverlayClose;
  }
};

/**
 * @method composeOnOverlayClose
 * @summary function sets the open and closng state
 * @param {object} overlayData - Object which contains data.
 * @param {object} methods - an object containing methods to be passed into the function.
 */
export const composeOnOverlayClose = ( overlayData, methods ) => {

  const { disableClose, e, data } = overlayData;
  const { onClose, setIsOpen } = methods;
  // if disableClose is true do not close.
  if( disableClose ){
    return;
  }
  onClose( e, data );
  setIsOpen( false );
};

/**
 * @method composeOnUserKeyPress
 * @summary function closes the overlay on escape key press
 * @param {object} data - Object which contains data.
 * @param {object} methods - an object containing methods to be passed into the function.
 */
export const composeOnUserKeyPress = ( data, methods ) => {
  const { disableClose, e } = data;
  const { onOverlayClose } = methods;
  if( disableClose ){
    return;
  }
  return onOverlayClose( e, { userCancelled: true } );
};

export const initiallizeOverlayOffset = ( data, methods ) => {
  const { offsetElement } = data;
  const { setOffsetPx, setIsCustom } = methods;
  if( !isFunction( offsetElement?.current?.getBoundingClientRect ) ){
    return;
  }

  const rect = offsetElement.current.getBoundingClientRect();

  // Update top offset
  setOffsetPx( `${rect.top + rect.height + 1}px` );

  // Tell overlay to render custom div offscreen + show backdrop
  setIsCustom( true );
};

/**
 * Handles logic for the back action button in the flyout header.
 *
 * @param {object} data
 * @param {object} data.backAction - Back action configuration.
 *
 * @param {object} methods
 * @param {function} [methods.invokeAction] - Function used to execute a GraphQL or custom action.
 * @param {function} [methods.backActionCallback] - Called before executing back action logic.
 *
 * @returns {void}
 */
export const handleOverlayBackAction = ( data, methods ) => {
  const { backAction } = data || {};
  const { invokeAction, backActionCallback } = methods || {};
  const { graphql, customHeaders, params, variables, method } = backAction || {};

  backActionCallback && backActionCallback();

  if( !graphql || !invokeAction ){
    return;
  }

  invokeAction( {
    graphql,
    customHeaders,
    method,
    params,
    variables
  } );
};

/**
 * Create a mapper between component alignment prop with palette alignment prop.
 *
 * @type {Object}
 * @property {string} right - aligns the flyout to right
 * @property {string} center - aligns the flyout to right
 * @property {string} left - aligns the flyout to left
 */
export const PALETTE_CONTENT_ALIGNMENT = {
  right: 'right',
  center: 'right',
  left: 'left'
};

/**
 * Sets the variant of content rendered for the Flyout.
 *
 * @type {Object}
 * @property {string} DEFAULT - default: uses OverlayDefaultContent component to render default Overlay with sticky header and footer
 * @property {string} CUSTOM - custom: can be used to configured the Flyout with custom content for header, body and footer
 */
export const PALETTE_VARIANT = {
  DEFAULT: 'default',
  CUSTOM: 'custom'
};

/**
 * Overlay Flyout class
 * @constant {string} OVERLAY_SCROLL_PARENT
 */
export const OVERLAY_SCROLL_PARENT = 'OverlayChild_scrollParent';

/**
 * Overlay Flyout class
 * @constant {string} OVERLAY_FLYOUT_SCROLL_BODY
 */
export const OVERLAY_SCROLL_BODY = 'OverlayChild_scrollBody';

/**
 * Overlay Flyout class
 * @constant {string} OVERLAY_SCROLL_FOOTER
 */
export const OVERLAY_SCROLL_FOOTER = 'OverlayChild_scrollFooter';

/**
 * Default props for OverlayFlyout.
 *
 * @type {object}
 * @property {'center'} alignment - Default alignment.
 * @property {'Close'} closeAccessibilityLabel - ARIA label for close button.
 */
export const defaultProps = {
  alignment: 'center',
  closeAccessibilityLabel: 'Close'
};

/**
  * Property type definitions
  * @typedef OverlayFlyoutProps
  * @type {object}
  * @property {string} [alignment='center'] - Sets flyout alignment modifier className. Maps to PalFlyout alignment. Default is 'center'.
  * @property {string} [closeAccessibilityLabel='Close'] - Sets the accessibility label for the close button. Default is 'Close'.
  * @property {function} onClose - Sets the onClose event on the dialog element, called when the overlay is requested to close (e.g., via ESC key, backdrop click, or close button).
  * @property {boolean} [showDynamicFlyoutHeader] - Sets a modifier class name indicating if the overlay header is managed dynamically by an external container (e.g., OverlayContainer).
  * @property {number} [offset] - Sets a top offset, potentially used when anchoring the flyout to another element. (Note: The provided code uses an 'offsetElement' prop for calculation, but 'offset' is in the propTypes).
  * @property {string} [ariaLabel] - Defines a string value that labels the current element for accessibility purposes.
  * @property {string} [ariaLabelledby] - Identifies the element(s) that labels the current element for accessibility purposes.
  * @property {object} [backAction] - Configuration object for a back action button.
  * @property {function} [backActionCallback] - Callback function for when the back action is performed.
  * @property {React.ReactNode} children - The content to be displayed inside the flyout.
  * @property {boolean} [crossButtonVisibility] - Controls the visibility of the internal close cross button (if not using a dynamic header).
  * @property {string} [customClassName] - A custom CSS class name to apply to the component and backdrop.
  * @property {boolean} [disableClose] - If true, prevents the overlay from closing via the ESC key or backdrop click.
  * @property {string} [headerText] - The text content for the flyout header.
  * @property {function} [customCloseEvent] - If provided, this function is called instead of `closeOverlay` when the close button is clicked.
  * @property {function} [invokeAction] - A function that can be used to invoke an action.
  * @property {object} [offsetElement] - A ref object pointing to an element used to calculate the top offset of the flyout. (Used internally by `useCustomOverlay`).
  */

/**
* @type {OverlayFlyoutProps}
*/
export const propTypes = {
  alignment: PropTypes.string,
  closeAccessibilityLabel: PropTypes.string,
  offset: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  showDynamicFlyoutHeader: PropTypes.bool,
  ariaLabel: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  backAction: PropTypes.object,
  backActionCallback: PropTypes.func,
  children: PropTypes.node,
  crossButtonVisibility: PropTypes.bool,
  customClassName: PropTypes.string,
  disableClose: PropTypes.bool,
  headerText: PropTypes.string,
  customCloseEvent: PropTypes.func,
  invokeAction: PropTypes.func,
  offsetElement: PropTypes.object
};

OverlayFlyout.propTypes = propTypes;
OverlayFlyout.defaultProps = defaultProps;
OverlayFlyout.displayName = 'OverlayFlyout';

export default OverlayFlyout;
